import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Radio, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { Card, CardContent } from "./ui/card";
import HourlyTrendStrip from "./HourlyTrendStrip";

/**
 * R1d.10 — Real-time roll-up of inbound public contact-form leads for the
 * last 60 minutes. Owner-only. Groups by service + campaign ref_slug.
 *
 * Truthful empty state: when total===0 we render an explicit "No new
 * inbound leads in the last hour" line — never a placeholder count.
 */
function relativeMinutes(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const mins = Math.max(0, Math.floor((now - then) / 60000));
  if (mins < 1) return "just now";
  if (mins === 1) return "1 min ago";
  return `${mins} mins ago`;
}

function prettifySlug(slug) {
  if (!slug) return "";
  return slug
    .replace(/_/g, "-")
    .split("-")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

export default function NewThisHourTile() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/owner/new-this-hour");
      setData(r.data);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const total = data?.total ?? 0;
  const byService = data?.by_service || [];
  const byRef = data?.by_ref || [];
  const recent = data?.recent || [];

  return (
    <section className="mt-8" data-testid="new-this-hour-tile">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Radio className={`w-4 h-4 ${total > 0 ? "text-erf-gold" : "text-erf-dark/40"}`} aria-hidden="true" />
          <h2 className="font-display font-semibold text-lg text-erf-dark">
            New This Hour
          </h2>
          <span
            className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/50"
            data-testid="new-this-hour-window"
          >
            last 60 min · live
          </span>
        </div>
        <Link
          to="/crm/leads?filter=new-this-hour"
          className="text-xs font-nav text-erf-gold flex items-center gap-1 min-h-[44px] hover:underline"
          data-testid="new-this-hour-view-all"
        >
          View all in CRM <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <Card className="border-erf-borderCream bg-erf-cream">
        <CardContent className="p-5">
          {loading && !data ? (
            <div className="font-mono text-xs text-erf-dark/60">Loading…</div>
          ) : err ? (
            <div className="text-xs text-red-700 font-mono" data-testid="new-this-hour-error">
              {String(err)}
            </div>
          ) : total === 0 ? (
            <div className="flex items-center gap-3 py-2">
              <div className="font-mono text-4xl font-semibold text-erf-dark/40 leading-none" data-testid="new-this-hour-total">
                0
              </div>
              <div className="text-xs font-nav text-erf-dark/60" data-testid="new-this-hour-empty">
                No new inbound leads in the last hour.
              </div>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-3">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/50 mb-1">
                  Inbound this hour
                </div>
                <div className="font-mono text-5xl font-semibold text-erf-dark leading-none" data-testid="new-this-hour-total">
                  {total}
                </div>
                <div className="text-[11px] font-nav text-erf-dark/60 mt-2">
                  {total === 1 ? "lead" : "leads"} via public contact form
                </div>
              </div>

              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/50 mb-2">
                  Top services
                </div>
                <ul className="space-y-1.5" data-testid="new-this-hour-by-service">
                  {byService.map((row) => (
                    <li
                      key={row.slug}
                      className="flex items-center justify-between text-xs font-nav"
                      data-testid={`new-this-hour-service-${row.slug}`}
                    >
                      <span className="text-erf-dark truncate pr-2">{row.label || prettifySlug(row.slug)}</span>
                      <span className="font-mono text-erf-dark/70">{row.count}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/50 mb-2">
                  Top campaigns · ref_slug
                </div>
                <ul className="space-y-1.5" data-testid="new-this-hour-by-ref">
                  {byRef.map((row) => (
                    <li
                      key={row.ref_slug}
                      className="flex items-center justify-between text-xs font-nav"
                      data-testid={`new-this-hour-ref-${row.ref_slug}`}
                    >
                      <span className="text-erf-dark truncate pr-2 font-mono">{row.ref_slug}</span>
                      <span className="font-mono text-erf-dark/70">{row.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {total > 0 && recent.length > 0 && (
            <div className="mt-5 border-t border-erf-borderCream pt-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/50 mb-2">
                Most recent
              </div>
              <ul className="space-y-1" data-testid="new-this-hour-recent">
                {recent.slice(0, 5).map((row) => (
                  <li
                    key={row.interaction_id}
                    className="flex items-center justify-between text-xs font-nav"
                    data-testid={`new-this-hour-recent-${row.interaction_id}`}
                  >
                    <Link
                      to={`/crm/leads/${row.lead_id}`}
                      className="text-erf-dark hover:text-erf-goldHover truncate pr-2"
                    >
                      <span className="font-semibold">{row.contact_name || "Anonymous"}</span>
                      <span className="text-erf-dark/50 mx-1.5">·</span>
                      <span className="font-mono text-erf-dark/70">{prettifySlug(row.service_slug)}</span>
                    </Link>
                    <span className="font-mono text-[10px] text-erf-dark/50">
                      {relativeMinutes(row.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* R1d.11 — subordinate 12-hour trend strip beneath the tile. */}
      <HourlyTrendStrip apiClient={api} />
    </section>
  );
}
